import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/clientWeb/components/ui/vcDialog"
import { Button } from "@/clientWeb/components/ui/button"
import { useTranslation } from "@/clientWeb/i18n/LanguageContext"

interface ConfirmDialogProps {
  open: boolean
  title: string
  description: string
  /** Styles the confirm button as destructive. */
  destructive?: boolean
  onConfirm: () => void
  onCancel: () => void
}

/**
 * A yes/no gate in front of an action that cannot be taken back.
 *
 * The VC app had no confirmation of any kind: the participants panel's bulk
 * moderator actions — mute everyone, stop every camera, stop every share, end
 * the meeting — all fired on a single click, and "End meeting" disconnected
 * every participant in the room from a button sitting next to three others.
 * Mobile had already grown a confirmation sheet for exactly these four.
 */
export function ConfirmDialog({
  open,
  title,
  description,
  destructive = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const t = useTranslation()

  return (
    <Dialog
      size="xs"
      open={open}
      // Covers the overlay click and Escape as well as the close button, all of
      // which should mean "no" — the safe answer is the one you get by walking
      // away from the question.
      onOpenChange={(next) => {
        if (!next) onCancel()
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="ghost" onClick={onCancel}>
            {t("common.cancel")}
          </Button>
          <Button
            variant={destructive ? "destructive" : "default"}
            onClick={onConfirm}
          >
            {t("common.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
